import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  UserInputType,
  Workflow,
  WorkflowExecutionStatus,
  WorkflowStep,
  WorkflowStepExecution,
  WorkflowStepExecutionStatus,
} from "@amzn/finspace-service-typescript-client";
import {
  Box,
  ColumnLayout,
  Container,
  ContentLayout,
  ExpandableSection,
  Header,
  Link,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from "@cloudscape-design/components";
import { Button } from "../common/Button";
import { ButtonDropdown } from "../common/ButtonDropdown";
import "./workflowRunPage.scss";
import "./ai-reasoning-pane.scss";
import { ProgressBar } from "../common/Loaders/ProgressBar";
import { ErrorScreen, LoadingScreen } from "../common/UIStates";
import { FiltersDisplay, InfoSection } from "../common/ReportComponents";
import Badge from "../common/Badge";
import { BadgeColor } from "../../constants/reportConstants";
import { getStatusBadgeColor } from "../../utils/statusUtils";
import { useWorkflowStore } from "../../store/workflow-store";
import { useGlobalStore } from "../../store/global-store";
import { useState as useReactState } from "react";
import {
  downloadFile,
  generateDownloadOptions,
  shouldEnableDownload,
} from "../../utils/workflowExecutionUtils";
import { deriveCategory } from "../../utils/activityTransformUtils";
import {
  WORKFLOW_TAGS,
  getWorkflowDisplayName,
} from "../../constants/workflow-tags";
import { NETPPM_STEP_IDS } from "../../constants/netppm-step-ids";
import { NETPPM_FAQ_URL } from "../../constants/netppm-constants";
import TreeTable from "../NetPPM/TreeTable";
import SelectedItems from "../NetPPM/TreeTable/SelectedItems";
import { useTreeSelectionStore } from "../../store/tree-table-store";
import { FinSpaceServiceApi } from "../../api/finspaceServiceApi";
import { formatTimestamp, formatDuration } from "../../utils/dateTimeUtils";
import { FormThemeProvider } from "../common/Forms/FormThemeProvider";
import { recordCustomEvent } from "../../providers/RumProvider";
import { logger } from "../../utils/logger";
import { StreamChunk } from "../common/AIReasoningStream/types";
import AIReasoningPane from "./AIReasoningPane";
import { Divider } from "antd";
import { getCurrentStage } from "../../../configuration/aws-exports";
import { useWorkflowCategoryValidation } from "../../hooks";

const POLLING_INTERVAL_MS = 5000; // 5 seconds
const PROGRESS_COMPLETE_PERCENT = 100;

type PhaseStatus =
  | "failed"
  | "skipped"
  | "completed"
  | "current"
  | "pending"
  | "timed out";

const WorkflowRunPage: React.FC = () => {
  const {
    spaceId: paramSpaceId,
    workflowId: paramWorkflowId,
    workflowExecutionId: paramExecutionId,
  } = useParams<{
    spaceId: string;
    workflowId: string;
    workflowExecutionId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedSpaceId, fetchSpaces } = useGlobalStore();
  const {
    workflows,
    error,
    fetchWorkflows,
    getWorkflow,
    getWorkflowExecution,
    retryWorkflowExecution,
    isRetrying,
  } = useWorkflowStore();
  const { selectedNodes, toggleSelection } = useTreeSelectionStore();

  // Determine if this is run or report mode based on URL
  const isReportMode = location.pathname.includes("/report/");

  // Helper function to determine if AI reasoning feature should be shown
  // Only enable in beta and development stages
  const shouldShowAIReasoningFeature = useCallback((): boolean => {
    const currentStage = getCurrentStage();
    console.log("currentStage", currentStage);
    // Explicitly handle undefined/null/empty stage - default to hiding feature
    return true
    // if (!currentStage) return false;
    // return currentStage === 'beta' || currentStage === 'dev' || currentStage === 'development';
  }, []);

  const [progress, setProgress] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [executionStatus, setExecutionStatus] = useState<string>("PENDING");
  const [workflowExecutionIdState, setWorkflowExecutionIdState] =
    useState<string>(paramExecutionId || "");
  const [spaceId, setSpaceId] = useState<string>("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string>("Starting...");
  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow | null>(null);
  const [currentExecution, setCurrentExecution] = useState<any>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [pollingKey, setPollingKey] = useState(0);
  const [streamChunks, setStreamChunks] = useState<StreamChunk[]>([]);
  const [isStreamPaneOpen, setIsStreamPaneOpen] = useState(shouldShowAIReasoningFeature());
  const [isPolling, setIsPolling] = useState(false);
  const isFirstFetchRef = useRef(true);
  const isFetchingRef = useRef(false);
  const lastFrameSummaryIdRef = useRef<string | undefined>();
  const hasInitialFetchStartedRef = useRef(false);

  // Sticky scroll state
  const [isSticky, setIsSticky] = useState(false);
  const [stickyTop, setStickyTop] = useState(20);
  const [stickyRight, setStickyRight] = useState(20);
  const [stickyWidth, setStickyWidth] = useState(400);
  const selectedItemsColumnRef = useRef<HTMLDivElement>(null);
  const selectedItemsStickyRef = useRef<HTMLDivElement>(null);

  const workflowId = paramWorkflowId;

  // Scroll listener for sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      const column = selectedItemsColumnRef.current;
      if (!column) return;

      const rect = column.getBoundingClientRect();
      const threshold = 20; // Distance from top before sticking

      // Check if column has scrolled past threshold
      if (rect.top <= threshold && !isSticky) {
        // Calculate position to maintain visual consistency
        const right = window.innerWidth - rect.right;
        setStickyTop(threshold);
        setStickyRight(right);
        setStickyWidth(rect.width);
        setIsSticky(true);
      } else if (rect.top > threshold && isSticky) {
        setIsSticky(false);
      }
    };

    // Listen to scroll on window
    window.addEventListener("scroll", handleScroll);
    // Also check on resize
    window.addEventListener("resize", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isSticky]);

  const statusRef = useRef<string>("PENDING");
  const spaceIdRef = useRef<string>("");
  const workflowIdRef = useRef<string>("");
  const workflowExecutionIdRef = useRef<string>("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const isCompletedStatus = (status?: string) =>
    status === WorkflowStepExecutionStatus.SUCCEEDED ||
    status === WorkflowStepExecutionStatus.EMPTY_RESULT ||
    status === WorkflowStepExecutionStatus.TIMED_OUT ||
    status === WorkflowStepExecutionStatus.FAILED;

  const getCompletedStepsCount = () => {
    return (
      currentExecution?.stepExecutions?.filter(
        (stepExec: WorkflowStepExecution) => isCompletedStatus(stepExec.status)
      ).length || 0
    );
  };

  const calculateProgress = (executionData: any) => {
    if (
      !executionData.executedWorkflow?.steps ||
      !executionData.stepExecutions
    ) {
      setProgress(0);
      return;
    }

    const totalSteps = executionData.executedWorkflow.steps.length;
    if (totalSteps === 0) {
      setProgress(0);
      return;
    }

    if (
      executionData.status ===
      WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED
    ) {
      setProgress(PROGRESS_COMPLETE_PERCENT);
      return;
    }

    const completedSteps = executionData.stepExecutions.filter(
      (stepExec: WorkflowStepExecution) => isCompletedStatus(stepExec.status)
    ).length;

    // Calculate progress based on actual step completion
    const calculatedProgress =
      (completedSteps / totalSteps) * PROGRESS_COMPLETE_PERCENT;
    setProgress(calculatedProgress);
  };

  const stepExecutionsMap = useMemo<
    Map<string | undefined, WorkflowStepExecution>
  >(() => {
    return new Map(
      currentExecution?.stepExecutions?.map((se: WorkflowStepExecution) => [
        se.stepId,
        se,
      ]) || []
    );
  }, [currentExecution?.stepExecutions]);

  // Calculate reasoning chunk counts for each step
  const stepReasoningCounts = useMemo(() => {
    const counts = new Map<string, number>();
    
    streamChunks.forEach((chunk) => {
      if (chunk.steps && chunk.steps.length > 0) {
        chunk.steps.forEach((stepId) => {
          counts.set(stepId, (counts.get(stepId) || 0) + 1);
        });
      }
    });
    
    return counts;
  }, [streamChunks]);

  // Handler for when a step is clicked - scrolls to and highlights matching reasoning chunks
  const handleStepClick = useCallback((stepId: string) => {
    const count = stepReasoningCounts.get(stepId) || 0;
    if (count === 0) return; // Don't do anything if no reasoning chunks
    
    // Trigger scroll and highlight in the reasoning pane
    // We'll pass this through props to AIReasoningPane/AIReasoningStream
    const event = new CustomEvent('scrollToStepReasoning', { 
      detail: { stepId } 
    });
    window.dispatchEvent(event);
  }, [stepReasoningCounts]);

  const getStepStatus = (step: WorkflowStep) => {
    const stepExecution = stepExecutionsMap.get(step.id);

    if (stepExecution?.status === WorkflowStepExecutionStatus.SUCCEEDED) {
      return (
        <div
          className="step-status-completed"
          aria-label="Step completed"
          role="status"
        >
          <span aria-hidden="true">✓</span>
        </div>
      );
    }

    if (
      stepExecution?.status === WorkflowStepExecutionStatus.EMPTY_RESULT ||
      stepExecution?.status === WorkflowStepExecutionStatus.TIMED_OUT
    ) {
      return (
        <div
          className="step-status-caution"
          aria-label="Step completed with empty result"
          role="status"
        >
          <span aria-hidden="true">!</span>
        </div>
      );
    }

    if (stepExecution?.status === WorkflowStepExecutionStatus.FAILED) {
      return (
        <div
          className="step-status-failed"
          aria-label="Step failed"
          role="status"
        >
          <span aria-hidden="true">✗</span>
        </div>
      );
    }

    if (stepExecution?.status === "User Input Required") {
      return <StatusIndicator type="warning" />;
    }

    if (stepExecution?.status === "In Progress") {
      return <Spinner aria-label="Step in progress" />;
    }

    return (
      <div
        className="step-status-pending"
        aria-label="Step pending"
        role="status"
      />
    );
  };

  const getPhaseStatus = (
    hasStarted: boolean,
    agentExecutionComplete: boolean,
    isCompleted: boolean,
    isFailed: boolean
  ) => {
    if (isFailed) return "failed";
    if (isCompleted) return "completed";
    if (agentExecutionComplete) return "completed";
    if (hasStarted) return "current";
    return "pending";
  };

  const getAgentExecutionStatus = (
    agentExecutionInProgress: boolean,
    agentExecutionComplete: boolean,
    agentFailed: boolean
  ): PhaseStatus => {
    if (agentFailed) return "failed";

    // Check if any step failed when all steps are complete
    if (agentExecutionComplete) {
      const hasFailedStep = currentExecution?.stepExecutions?.some(
        (stepExec: WorkflowStepExecution) =>
          stepExec.status === WorkflowStepExecutionStatus.FAILED
      );
      if (hasFailedStep) return "failed";

      console.log("Step Executions", currentExecution?.stepExecutions);
      const hasTimedOutStep = currentExecution?.stepExecutions?.some(
        (stepExec: WorkflowStepExecution) =>
          stepExec.status === WorkflowStepExecutionStatus.TIMED_OUT
      );
      if (hasTimedOutStep) return "timed out";
      return "completed";
    }

    if (agentExecutionInProgress) return "current";
    return "pending";
  };

  const isWaitingForUserInput = () => {
    return (
      currentExecution?.stepExecutions?.some(
        (stepExec: any) =>
          stepExec?.userInputRequest?.userInputType ===
            UserInputType.VARIANCE_TREE_NODE_SELECTION &&
          !stepExec?.userInputRequest?.input
      ) || false
    );
  };

  const getReportGenerationStatus = (
    agentExecutionComplete: boolean,
    isCompleted: boolean,
    isSkipped: boolean,
    reportFailed: boolean
  ): PhaseStatus => {
    const reportInProgress =
      executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_IN_PROGRESS;

    if (reportFailed) return "failed";
    if (isSkipped) return "skipped";
    if (isCompleted) return "completed";
    if (reportInProgress) return "current";
    return "pending";
  };

  const getWorkflowPhases = () => {
    const totalSteps = currentExecution?.executedWorkflow?.steps?.length || 0;
    const completedSteps = getCompletedStepsCount();
    const agentExecutionInProgress =
      executionStatus === WorkflowExecutionStatus.AGENT_EXECUTION_IN_PROGRESS;
    const agentExecutionCompleted =
      completedSteps === totalSteps && totalSteps > 0;
    const agentExecutionFailed =
      executionStatus === WorkflowExecutionStatus.AGENT_EXECUTION_FAILED;

    const reportGenerationSucceeded =
      executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED;
    const reportGenerationSkipped =
      executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED;
    const reportGenerationFailed =
      executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_FAILED;

    return {
      agentExecution: {
        status: getAgentExecutionStatus(
          agentExecutionInProgress,
          agentExecutionCompleted,
          agentExecutionFailed
        ),
        label: "Agent Execution",
        progress: totalSteps > 0 ? `${completedSteps}/${totalSteps}` : "0/0",
      },
      reportGeneration: {
        status: getReportGenerationStatus(
          agentExecutionCompleted,
          reportGenerationSucceeded,
          reportGenerationSkipped,
          reportGenerationFailed
        ),
        label: "Report Generation",
      },
    };
  };

  const getPhaseStatusIcon = (
    status: PhaseStatus,
    isAgentPhase: boolean = false
  ) => {
    switch (status) {
      case "completed":
        return (
          <div
            className="step-status-completed"
            aria-label="Phase completed"
            role="status"
          >
            <span aria-hidden="true">✓</span>
          </div>
        );
      case "skipped":
        return (
          <div
            className="step-status-caution"
            aria-label="Phase skipped"
            role="status"
          >
            <span aria-hidden="true">!</span>
          </div>
        );
      case "failed":
        return (
          <div
            className="step-status-failed"
            aria-label="Phase failed"
            role="status"
          >
            <span aria-hidden="true">✗</span>
          </div>
        );
      case "timed out":
        return (
          <div
            className="step-status-caution"
            aria-label="Phase timed out"
            role="status"
          >
            <span aria-hidden="true">!</span>
          </div>
        );
      case "current":
        // Show warning icon instead of spinner when waiting for user input in agent phase
        if (isAgentPhase && isWaitingForUserInput()) {
          return (
            <StatusIndicator type="warning">
              User input required
            </StatusIndicator>
          );
        }
        return <Spinner aria-label="Phase in progress" />;
      default:
        return (
          <div
            className="step-status-pending"
            aria-label="Phase pending"
            role="status"
          />
        );
    }
  };

  const renderWorkflowPhases = () => {
    const phases = getWorkflowPhases();

    return (
      <SpaceBetween direction="vertical" size="s">
        {/* Agent Execution Phase */}
        <ExpandableSection
          defaultExpanded
          headerText={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {getPhaseStatusIcon(phases.agentExecution.status, true)}
              <span>
                {phases.agentExecution.label} ({phases.agentExecution.progress})
              </span>
            </div>
          }
        >
          <div className="workflow-steps-container">
            <SpaceBetween direction="vertical" size="s">
              {currentExecution.executedWorkflow.steps.map(
                (step: WorkflowStep) => {
                  const stepExecution = stepExecutionsMap.get(step.id);
                  const timestamp = formatTimestamp(
                    stepExecution?.lastUpdatedTime
                  );
                  const duration = formatDuration(stepExecution?.durationInMs);
                  const reasoningCount = stepReasoningCounts.get(step.id || '') || 0;
                  const hasReasoning = reasoningCount > 0;

                  return (
                    <div key={step.id} className="workflow-step-row">
                      <SpaceBetween direction="vertical" size="s">
                        <SpaceBetween
                          direction="horizontal"
                          size="s"
                          alignItems="center"
                        >
                          {getStepStatus(step)}
                          <div
                            className={hasReasoning ? 'step-name-clickable' : ''}
                            onClick={() => step.id && hasReasoning && handleStepClick(step.id)}
                            style={{ 
                              cursor: hasReasoning ? 'pointer' : 'default'
                            }}
                          >
                            <Box>{step.name}</Box>
                          </div>
                          {timestamp && (
                            <Box variant="small" color="text-body-secondary">
                              {timestamp}
                              {duration && ` (${duration})`}
                            </Box>
                          )}
                        </SpaceBetween>
                        {renderUserInputSelection(step)}
                      </SpaceBetween>
                    </div>
                  );
                }
              )}
            </SpaceBetween>
          </div>
        </ExpandableSection>

        {/* Report Generation Phase */}
        <div className="workflow-phase-row">
          {getPhaseStatusIcon(phases.reportGeneration.status)}
          <Box>{phases.reportGeneration.label}</Box>
          {currentExecution?.endTime ? (
            <Box variant="small" color="text-body-secondary">
              {formatTimestamp(currentExecution.endTime)}
            </Box>
          ) : (
            <Box />
          )}
        </div>
      </SpaceBetween>
    );
  };

  // Pre-populate selected nodes from tree data when user input is required
  const hasInitializedSelectionRef = useRef<string>("");

  useEffect(() => {
    const userTreeStep = currentExecution?.executedWorkflow?.steps?.find(
      (step: WorkflowStep) => step.id === NETPPM_STEP_IDS.USER_TREE_SELECTION
    );

    if (!userTreeStep) return;

    const stepExecution: any = stepExecutionsMap.get(userTreeStep.id);
    if (!stepExecution?.userInputRequest) return;

    const hasInput = stepExecution?.userInputRequest?.input;
    const treeData = hasInput
      ? { root: stepExecution.userInputRequest.input.root }
      : stepExecution?.userInputRequest?.options;

    if (!treeData?.root) return;

    // Reset if we haven't initialized or if hasInput changed
    const currentKey = `${workflowExecutionIdState}-${
      hasInput ? "with-input" : "no-input"
    }`;
    if (hasInitializedSelectionRef.current !== currentKey) {
      // Clear existing selections before repopulating
      const currentSelections = Array.from(selectedNodes.values());
      currentSelections.forEach((node) => toggleSelection(node));

      const extractSelectedNodes = (
        node: any,
        parentKey: string = ""
      ): void => {
        const encodedName = encodeURIComponent(node.name);
        const nodeKey = parentKey ? `${parentKey}:${encodedName}` : encodedName;

        // If node is marked as selected and has a value (is selectable), add to store
        if (node.selected && node.value !== undefined) {
          const transformedNode = {
            key: nodeKey,
            name: node.name,
            metadata: node.metadata,
            value: node.value,
            selected: true,
            level: 0,
            hasChildren: Boolean(node.children && node.children.length > 0),
            isSelectable: true,
            displayValue: node.value?.toString(),
            rawValue: node.value,
          };
          toggleSelection(transformedNode);
        }

        // Recursively process children
        if (node.children && node.children.length > 0) {
          node.children.forEach((child: any) =>
            extractSelectedNodes(child, nodeKey)
          );
        }
      };

      extractSelectedNodes(treeData.root);
      hasInitializedSelectionRef.current = currentKey;
    }
  }, [
    currentExecution?.stepExecutions,
    stepExecutionsMap,
    workflowExecutionIdState,
  ]);

  const renderUserInputSelection = (step: WorkflowStep) => {
    const stepExecution: any = stepExecutionsMap.get(step.id);

    if (
      stepExecution?.userInputRequest?.userInputType !==
      UserInputType.VARIANCE_TREE_NODE_SELECTION
    ) {
      return null;
    }

    const hasInput = stepExecution?.userInputRequest?.input;

    const treeData = hasInput
      ? { root: stepExecution.userInputRequest.input.root }
      : stepExecution?.userInputRequest?.options;

    if (!treeData?.root) {
      return null;
    }

    const buildTreeWithSelection = (node: any, parentKey: string = ""): any => {
      const encodedName = encodeURIComponent(node.name);
      const nodeKey = parentKey ? `${parentKey}:${encodedName}` : encodedName;
      const isSelected = selectedNodes.has(nodeKey);

      const result: any = {
        name: node.name,
        metadata: node.metadata,
        selected: isSelected,
      };

      if (node.value !== undefined) {
        result.value = node.value;
      }

      if (node.children && node.children.length > 0) {
        result.children = node.children.map((child: any) =>
          buildTreeWithSelection(child, nodeKey)
        );
      }

      return result;
    };

    const extractSelectedNodesFromTree = (
      node: any,
      parentKey: string = ""
    ): any[] => {
      const encodedName = encodeURIComponent(node.name);
      const nodeKey = parentKey ? `${parentKey}:${encodedName}` : encodedName;
      const selected: any[] = [];

      if (node.selected && node.value !== undefined) {
        selected.push({
          key: nodeKey,
          name: node.name,
          metadata: node.metadata,
          value: node.value,
          selected: true,
          level: 0,
          hasChildren: Boolean(node.children && node.children.length > 0),
          isSelectable: true,
          displayValue: node.value?.toString(),
          rawValue: node.value,
        });
      }

      if (node.children && node.children.length > 0) {
        node.children.forEach((child: any) => {
          selected.push(...extractSelectedNodesFromTree(child, nodeKey));
        });
      }

      return selected;
    };

    const handleApply = async () => {
      setIsApplying(true);
      try {
        const inputData = {
          root: buildTreeWithSelection(treeData.root),
        };

        await FinSpaceServiceApi.provideUserInput({
          spaceId: spaceId,
          workflowId: workflowId!,
          workflowExecutionId: workflowExecutionIdState,
          stepId: step.id,
          input: inputData,
        });

        // Immediately refetch execution data to get updated state with hasInput
        const response = await getWorkflowExecution({
          spaceId: spaceId,
          workflowId: workflowId!,
          workflowExecutionId: workflowExecutionIdState,
        });

        const executionData = response?.result || response;
        if (executionData?.status) {
          handleExecutionUpdate(executionData);
        }
      } catch (error) {
        logger.error("Failed to provide user input:", error as Error);
      } finally {
        setIsApplying(false);
      }
    };

    const selectedNodesForDisplay = hasInput
      ? extractSelectedNodesFromTree(treeData.root)
      : Array.from(selectedNodes.values());

    return (
      <Box margin={{ top: "xs" }}>
        <Container>
          <div style={{ width: "100%", maxWidth: "100%", overflow: "auto" }}>
            <SpaceBetween direction="vertical" size="m">
              <Box variant="p" color="text-body-secondary">
                {hasInput
                  ? "User has selected the following variance tree nodes for detailed analysis"
                  : <>This hierarchical tree breaks down NetPPM year-over-year variance, allowing you to drill down from top-level metrics to vendor-level drivers. Key drivers (accounting for ~80% of variance) have been automatically selected by the Pareto algorithm. You can deselect these items or add more nodes as needed. <strong>To start from scratch and manually select only the nodes you want, simply deselect the top-level "NetPPM" node and all child nodes will automatically deselect as well.</strong> All selected nodes will be included in your generated commentary. Click 'Apply' below when your selections are complete to generate the commentary.</>}
              </Box>
              <FormThemeProvider>
                <div
                  className={`tree-selection-layout ${
                    isStreamPaneOpen ? "ai-reasoning-open" : ""
                  }`}
                >
                  <div className="tree-table-column">
                    <TreeTable
                      key={`${workflowExecutionIdState}-${step.id}-${
                        hasInput ? "locked" : "editable"
                      }`}
                      data={treeData}
                      loading={false}
                      disabled={hasInput}
                    />
                  </div>
                  <div
                    className="selected-items-column"
                    ref={selectedItemsColumnRef}
                  >
                    <div
                      className={`selected-items-sticky ${
                        isSticky ? "is-fixed" : ""
                      }`}
                      style={
                        isSticky
                          ? {
                              position: "fixed",
                              top: `${stickyTop}px`,
                              right: `${stickyRight}px`,
                              width: `${stickyWidth}px`,
                              zIndex: 100,
                            }
                          : undefined
                      }
                    >
                      <SelectedItems selectedNodes={selectedNodesForDisplay} />
                    </div>
                  </div>
                </div>
              </FormThemeProvider>
              <Box float="right">
                <Button
                  variant="primary"
                  onClick={handleApply}
                  disabled={isApplying || hasInput}
                >
                  Apply
                </Button>
              </Box>
            </SpaceBetween>
          </div>
        </Container>
      </Box>
    );
  };

  const getExecutionHeaderText = () => {
    switch (executionStatus) {
      case WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED:
        return "Execution Succeeded";
      case WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED:
        return "Execution Completed (No Report)";
      case WorkflowExecutionStatus.AGENT_EXECUTION_FAILED:
      case WorkflowExecutionStatus.REPORT_GENERATION_FAILED:
        return "Execution Failed";
      default:
        return "Execution Progress";
    }
  };

  const updateStatusAndNavigation = (status: string) => {
    switch (status) {
      case WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED:
      case WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED:
        setProgress(PROGRESS_COMPLETE_PERCENT);
        stopPolling();
        // Auto-navigate to report page if currently on run page
        if (!isReportMode) {
          const category =
            deriveCategory(
              currentWorkflow?.tags || workflow?.tags
            )?.toLowerCase() || "mfr";
          navigate(
            `/reports-and-analysis/space/${spaceIdRef.current}/${category}/${workflowIdRef.current}/report/${workflowExecutionIdRef.current}`,
            {
              state: {
                selectedPeriod,
                spaceId: spaceIdRef.current,
              },
            }
          );
        }
        break;
      case WorkflowExecutionStatus.AGENT_EXECUTION_FAILED:
      case WorkflowExecutionStatus.REPORT_GENERATION_FAILED:
      case WorkflowExecutionStatus.AGENT_EXECUTION_TIMED_OUT:
        stopPolling();
        break;
      default:
        break;
    }
  };

  const handleExecutionUpdate = (executionData: {
    status: string;
    stepExecutions?: Array<{ status: string }>;
  }) => {
    setCurrentExecution(executionData);
    setExecutionStatus(executionData.status);

    // Calculate progress based on step completion
    calculateProgress(executionData);

    // Update status message based on workflow status
    updateStatusAndNavigation(executionData.status);
  };

  // Fetch spaces on mount (handles page refresh)
  useEffect(() => {
    fetchSpaces();
  }, []);

  // Update refs
  statusRef.current = executionStatus;
  spaceIdRef.current = spaceId;
  workflowIdRef.current = workflowId || "";
  workflowExecutionIdRef.current = workflowExecutionIdState || "";

  // Get execution details from navigation state or use URL params
  useEffect(() => {
    const state = location.state as any;
    if (state?.selectedPeriod) {
      setSelectedPeriod(state.selectedPeriod);
    }
    if (state?.workflowExecutionId) {
      setWorkflowExecutionIdState(state.workflowExecutionId);
    } else if (paramExecutionId) {
      setWorkflowExecutionIdState(paramExecutionId);
    }
    // Use paramSpaceId from URL first, then fall back to state or selectedSpaceId
    if (paramSpaceId) {
      setSpaceId(paramSpaceId);
    } else if (state?.spaceId) {
      setSpaceId(state.spaceId);
    } else if (selectedSpaceId) {
      setSpaceId(selectedSpaceId);
    }
  }, [location.state, paramExecutionId, paramSpaceId, selectedSpaceId]);

  // Start polling when we have all required data (only in run mode)
  useEffect(() => {
    logger.info("Polling useEffect triggered", {
      spaceId,
      workflowId,
      workflowExecutionIdState,
      isInitialLoading,
      isReportMode,
      pollingKey,
    });

    if (
      !spaceId ||
      !workflowId ||
      !workflowExecutionIdState ||
      isInitialLoading
    ) {
      logger.info("Polling blocked - missing data or still loading");
      return;
    }

    const poll = async () => {
      logger.info("Polling...");
      if (
        !spaceIdRef.current ||
        !workflowIdRef.current ||
        !workflowExecutionIdRef.current
      )
        return;

      try {
        const response = await getWorkflowExecution({
          spaceId: spaceIdRef.current,
          workflowId: workflowIdRef.current,
          workflowExecutionId: workflowExecutionIdRef.current,
        });

        logger.info("Response: {}", response);
        const executionData = response?.result || response;

        if (executionData?.status) {
          handleExecutionUpdate(executionData);
        }
      } catch (error) {
        logger.error(
          "Failed to get workflow execution status:",
          error as Error
        );
      }
    };

    if (!isReportMode) {
      logger.info("Starting polling...");
      // Only start polling in run mode, after initial loading is complete
      intervalRef.current = setInterval(poll, POLLING_INTERVAL_MS);
    } else {
      logger.info("Report mode - polling disabled");
    }

    return () => {
      stopPolling();
    };
  }, [
    spaceId,
    workflowId,
    workflowExecutionIdState,
    isReportMode,
    isInitialLoading,
    pollingKey,
  ]);

  // Poll for execution frame summaries
  useEffect(() => {
    if (!workflowExecutionIdState || !isStreamPaneOpen) return;

    const isCompleted =
      executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED ||
      executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_FAILED ||
      executionStatus === WorkflowExecutionStatus.AGENT_EXECUTION_FAILED ||
      executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED ||
      executionStatus === WorkflowExecutionStatus.AGENT_EXECUTION_TIMED_OUT;

    if (isCompleted) {
      setIsPolling(false);
      if (hasInitialFetchStartedRef.current) return;
    } else {
      setIsPolling(true);
    }

    const fetchFrameSummaries = async () => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        const response =
          await FinSpaceServiceApi.getAgentExecutionFrameSummaries(
            workflowExecutionIdState,
            lastFrameSummaryIdRef.current
          );

        if (
          response.executionFrameSummaries &&
          response.executionFrameSummaries.length > 0
        ) {
          const newChunks: StreamChunk[] = response.executionFrameSummaries.map(
            (frame) => ({
              id: frame.executionFrameSummaryId!,
              type: "reasoning",
              content: frame.text!,
              //@ts-ignore
              tools: frame.tools,
              //@ts-ignore
              steps: frame.steps || []
            })
          );

          if (isFirstFetchRef.current) {
            isFirstFetchRef.current = false;
          }

          setStreamChunks((prev) => [...prev, ...newChunks]);

          const lastId =
            response.executionFrameSummaries[
              response.executionFrameSummaries.length - 1
            ].executionFrameSummaryId;
          lastFrameSummaryIdRef.current = lastId;
        }
      } catch (error) {
        logger.error(
          "Failed to fetch execution frame summaries",
          error as Error
        );
      } finally {
        isFetchingRef.current = false;
      }
    };

    if (isCompleted) {
      hasInitialFetchStartedRef.current = true;
      fetchFrameSummaries();
    } else {
      fetchFrameSummaries();
      const interval = setInterval(fetchFrameSummaries, POLLING_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [workflowExecutionIdState, executionStatus, isStreamPaneOpen]);

  // Fetch workflow details and execution data (handles page refresh)
  useEffect(() => {
    const loadInitialData = async () => {
      if (!spaceId || !workflowId || !workflowExecutionIdState) return;

      try {
        // Use spaceId from URL
        const workflow = await getWorkflow(spaceId, workflowId);
        setCurrentWorkflow(workflow || null);

        // Load execution data using spaceId from URL
        const response = await getWorkflowExecution({
          spaceId: spaceId,
          workflowId: workflowId,
          workflowExecutionId: workflowExecutionIdState,
        });

        const executionData = response?.result || response;
        if (executionData?.status) {
          handleExecutionUpdate(executionData);
        }
      } catch (error) {
        logger.error("Failed to load initial data:", error as Error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialData();
  }, [
    spaceId,
    workflowId,
    workflowExecutionIdState,
    getWorkflow,
    getWorkflowExecution,
  ]);

  const workflow =
    currentWorkflow || workflows.find((w) => w.id === workflowId);

  // Validate URL category matches workflow's actual category
  const { isValid: isUrlCategoryValid, correctPath } = useWorkflowCategoryValidation(
    location.pathname,
    workflow?.tags
  );

  // Redirect to correct URL if category mismatch detected
  useEffect(() => {
    if (!isUrlCategoryValid && correctPath) {
      navigate(correctPath, { replace: true, state: location.state });
    }
  }, [isUrlCategoryValid, correctPath, navigate, location.state]);

  // Generate download options from current execution
  const downloadOptions = generateDownloadOptions(currentExecution);
  const isDownloadEnabled = shouldEnableDownload(currentExecution);

  const handleDownload = async (option: { id: string; label: string }) => {
    const selectedOption = downloadOptions.find(
      (opt) => opt.label === option.label
    );
    if (selectedOption) {
      try {
        recordCustomEvent("ReportDownload", {
          reportType: deriveCategory(workflow?.tags),
          downloadOption: option.label,
        });
        await downloadFile(selectedOption.s3URI);
      } catch (error) {
        logger.error("Download failed:", error as Error);
      }
    }
  };

  const handleBack = () => {
    const category = deriveCategory(workflow?.tags)?.toLowerCase();
    navigate(`/reports-and-analysis/${category}/view`);
  };

  const handleRetry = async () => {
    if (!spaceId || !workflowId || !workflowExecutionIdState) {
      logger.error("Missing required IDs for retry");
      return;
    }
    navigate(
      `/reports-and-analysis/space/${spaceIdRef.current}/${category}/${workflowIdRef.current}/run/${workflowExecutionIdRef.current}`
    );
    try {
      await retryWorkflowExecution(
        spaceId,
        workflowId,
        workflowExecutionIdState
      );

      // Retry happens in place - refresh the current execution data immediately
      const response = await getWorkflowExecution({
        spaceId: spaceId,
        workflowId: workflowId,
        workflowExecutionId: workflowExecutionIdState,
      });

      const executionData = response?.result || response;
      if (executionData) {
        // Update state directly without calling handleExecutionUpdate
        // to avoid the race condition where stopPolling() gets called
        setCurrentExecution(executionData);
        setExecutionStatus(executionData.status);
        calculateProgress(executionData);

        // Restart polling by incrementing the polling key
        // This triggers the polling useEffect to re-run and start a new interval
        setPollingKey((prev) => prev + 1);
      }
    } catch (error) {
      logger.error("Failed to retry workflow execution:", error as Error);
    }
  };

  const shouldShowRetryButton = () => {
    // Only show retry button if at least one step has failed
    const hasFailedStep = currentExecution?.stepExecutions?.some(
      (stepExec: WorkflowStepExecution) =>
        stepExec.status === WorkflowStepExecutionStatus.FAILED ||
        stepExec.status === WorkflowStepExecutionStatus.TIMED_OUT
    );

    return (
      hasFailedStep &&
      (executionStatus ===
        WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED ||
        executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED ||
        executionStatus === WorkflowExecutionStatus.AGENT_EXECUTION_TIMED_OUT ||
        executionStatus === WorkflowExecutionStatus.AGENT_EXECUTION_FAILED ||
        executionStatus === WorkflowExecutionStatus.REPORT_GENERATION_FAILED)
    );
  };

  if (isInitialLoading) {
    return <LoadingScreen message="Loading Workflow Run..." />;
  }

  if (error) {
    return (
      <ErrorScreen
        title="Failed to run workflow"
        message="Unable to start workflow execution. Please try again later."
        onButtonClick={handleBack}
      />
    );
  }

  if (!workflow) {
    return (
      <ErrorScreen
        title="Workflow not found"
        message="The requested workflow could not be found"
        onButtonClick={handleBack}
      />
    );
  }

  const category = deriveCategory(workflow?.tags);

  // Get actual configuration from workflow input
  const getActualFilters = (category: string): Record<string, any> => {
    const workflowInput = currentExecution?.executedWorkflow?.input || {};

    switch (category) {
      case WORKFLOW_TAGS.MFR.value:
        return {
          Marketplace:
            workflowInput.marketplace?.map((item: any) => item.label) ||
            workflowInput.marketplace,
          Channel:
            workflowInput.channel?.map((item: any) => item.label) ||
            workflowInput.channel,
          "Product Line":
            workflowInput.product_lines?.map((item: any) => item.label) ||
            workflowInput.product_lines,
          Period:
            workflowInput.period?.map((item: any) => item.label) ||
            workflowInput.period,
          "Base Scenario":
            workflowInput.base_scenario?.map((item: any) => item.label) ||
            workflowInput.base_scenario,
          "Comparison Scenario":
            workflowInput.comparison_scenario?.map((item: any) => item.label) ||
            workflowInput.comparison_scenario,
          Currency:
            workflowInput.currency?.map((item: any) => item.label) ||
            workflowInput.currency,
          "Data Source":
            workflowInput.data_source?.map((item: any) => item.label) ||
            workflowInput.data_source,
        };
      case WORKFLOW_TAGS.PPM.value:
        return {
          Region:
            workflowInput.region?.map((item: any) => item.label) ||
            workflowInput.region,
          "Product Family":
            workflowInput.product_family?.map((item: any) => item.label) ||
            workflowInput.product_family,
          "GL Code":
            workflowInput.gl_code?.map((item: any) => item.label) ||
            workflowInput.gl_code,
          "Deep Dive Dimension":
            workflowInput.deep_dive_dimension?.map((item: any) => item.label) ||
            workflowInput.deep_dive_dimension,
          Period:
            workflowInput.period?.map((item: any) => item.label) ||
            workflowInput.period,
          Scenarios:
            workflowInput.scenarios?.map((item: any) => item.label) ||
            workflowInput.scenarios,
        };
      default:
        return {
          Period:
            workflowInput.period?.map((item: any) => item.label) ||
            workflowInput.period,
        };
    }
  };

  const workflowInfoItems: any[] = [
    // Execution start time
    {
      type: "text" as const,
      content: `Execution Started: ${
        formatTimestamp(currentExecution?.startTime) || "Not started"
      }`,
    },
    // Execution end time or in-progress message
    ...(currentExecution?.endTime
      ? [
          {
            type: "text" as const,
            content: `Execution Completed: ${formatTimestamp(
              currentExecution.endTime
            )}`,
          },
        ]
      : [
          {
            type: "text" as const,
            content: `Workflow is running, you will receive an email upon completion`,
          },
        ]),
    // Duration (only show if durationInMs exists)
    ...(currentExecution?.durationInMs
      ? [
          {
            type: "text" as const,
            content: `Duration: ${formatDuration(
              currentExecution.durationInMs
            )}`,
          },
        ]
      : []),
  ];

  const actualFilters = getActualFilters(category || "");

  // Helper function to generate filter preview for header description
  const generateFilterPreview = (filters: Record<string, any>): string => {
    const entries = Object.entries(filters).slice(0, 4); // Get first 4 fields

    return entries
      .map(([key, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return null;
        }

        let displayValue: string;
        if (Array.isArray(value)) {
          const firstItem = value[0];
          const remaining = value.length - 1;
          displayValue =
            remaining > 0 ? `${firstItem} +${remaining} more` : firstItem;
        } else {
          displayValue = String(value);
        }

        return `${key}: ${displayValue}`;
      })
      .filter(Boolean)
      .join(" | ");
  };

  const filterPreview = generateFilterPreview(actualFilters);

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="secondary"
                icon="arrow-left"
                onClick={handleBack}
              >
                Back
              </Button>
              {shouldShowRetryButton() && (
                <Button
                  variant="secondary"
                  icon="refresh"
                  onClick={handleRetry}
                  disabled={isRetrying}
                  loading={isRetrying}
                >
                  Retry
                </Button>
              )}
              <ButtonDropdown
                variant="primary"
                disabled={!isDownloadEnabled}
                options={downloadOptions.map((option) => ({
                  label: option.label,
                  id: option.label,
                }))}
                onItemClick={handleDownload}
              >
                Download
              </ButtonDropdown>
              {shouldShowAIReasoningFeature() && !isStreamPaneOpen && (
                <Button
                  variant="primary"
                  onClick={() => setIsStreamPaneOpen(true)}
                  useGradient
                  icon="gen-ai"
                  size="medium"
                >
                  AI Reasoning
                </Button>
              )}
            </SpaceBetween>
          }
        >
          <SpaceBetween
            direction="horizontal"
            size="xs"
            data-component-id="workflow-run-page-header"
          >
            {workflow.name}
            <Badge variant="category" color={BadgeColor.BLUE}>
              {getWorkflowDisplayName(category || "")}
            </Badge>
            <Badge
              variant="status"
              color={getStatusBadgeColor(
                executionStatus as WorkflowExecutionStatus,
                currentExecution?.stepExecutions
              )}
            >
              {executionStatus}
            </Badge>
            {category === WORKFLOW_TAGS.PPM.value && (
              <Link
                href={NETPPM_FAQ_URL}
                external
                fontSize="body-s"
              >
                FAQs
              </Link>
            )}
          </SpaceBetween>
        </Header>
      }
    >
      <div style={{ display: "flex", gap: "20px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SpaceBetween direction="vertical" size="s">
            {/* Configuration Details - Right Column */}
            <ExpandableSection
              variant="container"
              headerText="Workflow Configuration"
              headerDescription={filterPreview}
            >
              <FiltersDisplay filters={actualFilters} />
            </ExpandableSection>
            {/* Workflow Info Section */}
            <InfoSection items={workflowInfoItems} />

            <ColumnLayout columns={1} variant="text-grid">
              {/* Execution Progress - Left Column */}
              <Container
                header={
                  <Box>
                    <Box variant="strong" fontSize="heading-s">
                      {getExecutionHeaderText()}
                    </Box>
                  </Box>
                }
              >
                <SpaceBetween direction="vertical" size="m">
                  <div
                    className={
                      executionStatus ===
                      WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED
                        ? "progress-bar-succeeded"
                        : executionStatus ===
                          WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED
                        ? "progress-bar-skipped"
                        : executionStatus ===
                            WorkflowExecutionStatus.AGENT_EXECUTION_FAILED ||
                          executionStatus ===
                            WorkflowExecutionStatus.REPORT_GENERATION_FAILED
                        ? "progress-bar-failed"
                        : ""
                    }
                  >
                    <ProgressBar
                      progress={progress}
                      width="100%"
                      height="8px"
                      showShimmer={
                        progress < PROGRESS_COMPLETE_PERCENT &&
                        executionStatus !==
                          WorkflowExecutionStatus.AGENT_EXECUTION_FAILED &&
                        executionStatus !==
                          WorkflowExecutionStatus.REPORT_GENERATION_FAILED
                      }
                    />
                  </div>
                  {currentExecution?.executedWorkflow?.steps?.length > 0 &&
                    renderWorkflowPhases()}
                </SpaceBetween>
              </Container>
            </ColumnLayout>
          </SpaceBetween>
        </div>

        {isStreamPaneOpen && (
          <AIReasoningPane
            chunks={streamChunks}
            isPolling={isPolling}
            streamSpeed={200}
            autoScroll={true}
            streamingMode="smart"
            showThinkingAnimation={isPolling}
            disableStreaming={
              executionStatus ===
                WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED ||
              executionStatus ===
                WorkflowExecutionStatus.REPORT_GENERATION_FAILED ||
              executionStatus ===
                WorkflowExecutionStatus.AGENT_EXECUTION_FAILED ||
              executionStatus ===
                WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED ||
              executionStatus ===
                WorkflowExecutionStatus.AGENT_EXECUTION_TIMED_OUT
            }
            onClose={() => {
              setIsStreamPaneOpen(false);
              setStreamChunks([]);
              isFirstFetchRef.current = true;
              lastFrameSummaryIdRef.current = undefined;
              hasInitialFetchStartedRef.current = false;
              setIsPolling(false);
            }}
          />
        )}
      </div>
    </ContentLayout>
  );
};

export default WorkflowRunPage;
